import { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import {
  Table,
  Input,
  Button,
  Space,
  Typography,
  Alert,
  Tooltip,
  Dropdown,
  Modal,
  message,
  Form,
  Switch,
  Tag,
  type MenuProps,
} from 'antd';
import {
  ReloadOutlined,
  CodeOutlined,
  PlusOutlined,
  RightOutlined,
  MoreOutlined,
  DeleteOutlined,
  CopyOutlined,
  EditOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useStore } from '@/stores';
import type { SystemPromptDto } from '@/api/types';
import { colors } from '@/theme/tokens';
import { trackEvent } from '@/lib/telemetry';
import { PageHeader } from '@/components/PageHeader';
import { StatusPill } from '@/components/StatusPill';
import { BrandedEmpty } from '@/components/BrandedEmpty';
import { SectionCard } from '@/components/SectionCard';
import { HelpHotspot } from '@/features/help/HelpHotspot';

const { Text, Paragraph } = Typography;

// Library of System Prompts — instruction blocks composed into agent prompts
// via the capability chain (SystemPromptAiCapability → AiCapability → AiAgentCapability).
// Each row shows title, tags, and the "Global for" user types (Patient / Provider).

const USER_TYPE_LABEL: Record<string, string> = {
  Patient: 'Patient',
  Provider: 'Provider',
  Caregiver: 'Caregiver',
  Resident: 'Resident',
};

function userTypeName(t: unknown): string {
  if (typeof t === 'string') return USER_TYPE_LABEL[t] ?? t;
  if (typeof t === 'number') {
    // Server may serialize ConversationUserType enum as int
    return ['Provider', 'Patient', 'Caregiver', 'Resident'][t] ?? `Type ${t}`;
  }
  if (t && typeof t === 'object' && 'name' in t) return String((t as { name: string }).name);
  return String(t);
}

export const SystemPromptsPage = observer(function SystemPromptsPage() {
  const { systemPromptStore: store, merchantContextStore: ctx, themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState<SystemPromptDto | null>(null);

  // Re-fetch on every page mount. Without this, the page relies on the
  // store's reaction-on-merchantId-change, which doesn't re-fire when the
  // user navigates away and back. Same post-deploy stale-state failure
  // mode that AgentsListPage had — see that file's comment for the full
  // narrative.
  useEffect(() => {
    if (ctx.selectedMerchantId != null) {
      void store.fetch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (ctx.selectedMerchantId == null) {
    return (
      <SectionCard>
        <BrandedEmpty
          icon={<CodeOutlined />}
          title="Pick a merchant to begin"
          description="Use the merchant switcher in the top bar to scope the system prompt library."
          size="lg"
        />
      </SectionCard>
    );
  }

  function openEdit(row: SystemPromptDto | null): void {
    setEditing(row);
    setEditorOpen(true);
    trackEvent('SystemPrompt.EditOpened', { id: row?.id ?? 'new' });
  }

  function moreMenu(row: SystemPromptDto): MenuProps {
    return {
      items: [
        {
          key: 'edit',
          icon: <EditOutlined />,
          label: 'Edit instructions',
          onClick: () => openEdit(row),
        },
        {
          key: 'copy',
          icon: <CopyOutlined />,
          label: `Copy id #${row.id}`,
          onClick: () => {
            void navigator.clipboard?.writeText(String(row.id));
            message.success('Id copied');
          },
        },
        { type: 'divider' as const },
        {
          key: 'delete',
          icon: <DeleteOutlined />,
          label: 'Delete prompt',
          danger: true,
          onClick: () => {
            Modal.confirm({
              title: 'Delete this system prompt?',
              content: `"${row.title ?? row.name ?? `#${row.id}`}" will be removed. Agents using it will lose this instruction block.`,
              okText: 'Delete',
              okButtonProps: { danger: true },
              onOk: async () => {
                try {
                  await store.remove(row.id);
                  message.success('System prompt deleted');
                  trackEvent('SystemPrompt.Deleted', { id: row.id });
                } catch (err) {
                  message.error(err instanceof Error ? err.message : 'Delete failed');
                }
              },
            });
          },
        },
      ],
    };
  }

  const columns: ColumnsType<SystemPromptDto> = [
    {
      title: 'Title',
      key: 'title',
      sorter: (a, b) => (a.title ?? '').localeCompare(b.title ?? ''),
      render: (_text, row) => (
        <div>
          <div
            style={{
              fontSize: 13.5,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
              letterSpacing: '-0.005em',
            }}
          >
            {row.title || row.name || (
              <span style={{ fontStyle: 'italic', opacity: 0.7 }}>(untitled)</span>
            )}
          </div>
          {row.baseInstructions && (
            <div
              style={{
                fontSize: 11.5,
                color: isDark ? colors.slate[400] : colors.slate[500],
                fontFamily: "'JetBrains Mono', monospace",
                marginTop: 3,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                maxWidth: 480,
              }}
            >
              {row.baseInstructions.slice(0, 100)}
              {row.baseInstructions.length > 100 ? '…' : ''}
            </div>
          )}
        </div>
      ),
    },
    {
      title: (
        <Tooltip title="Free-text labels you add to find this prompt later. e.g. 'triage', 'oncology', 'patient-education'.">
          <span>Tags</span>
        </Tooltip>
      ),
      key: 'tags',
      width: 240,
      render: (_text, row) => {
        const tags = row.tags ?? [];
        if (tags.length === 0) return <Text type="secondary">—</Text>;
        return (
          <Space size={4} wrap>
            {tags.slice(0, 4).map((t) => (
              <Tag
                key={t}
                style={{
                  fontSize: 11,
                  fontFamily: "'JetBrains Mono', monospace",
                  background: isDark ? colors.slate[700] : colors.slate[100],
                  color: isDark ? colors.slate[200] : colors.slate[600],
                  borderColor: 'transparent',
                  margin: 0,
                }}
              >
                {t}
              </Tag>
            ))}
            {tags.length > 4 && (
              <Text type="secondary" style={{ fontSize: 11 }}>
                +{tags.length - 4}
              </Text>
            )}
          </Space>
        );
      },
    },
    {
      title: (
        <Tooltip title="Which user audience the agent shows this prompt's guidance to. 'All users' means no audience filter — the prompt applies to everyone.">
          <span>Visible to</span>
        </Tooltip>
      ),
      key: 'globalFor',
      width: 200,
      render: (_text, row) => {
        const types = (row.userTypes ?? []) as unknown[];
        if (types.length === 0) {
          return <StatusPill tone="slate">All users</StatusPill>;
        }
        return (
          <Space size={4} wrap>
            {types.map((t, i) => {
              const name = userTypeName(t);
              const tone = name === 'Patient' ? 'ink' : name === 'Provider' ? 'violet' : 'amber';
              return (
                <StatusPill key={i} tone={tone}>
                  {name}
                </StatusPill>
              );
            })}
          </Space>
        );
      },
    },
    {
      title: (
        <Tooltip title="When the agent reads this prompt. 'Always' means it's read on every turn that uses the linked capability. 'Only when needed' means it's loaded conditionally based on a runtime rule.">
          <span>When loaded</span>
        </Tooltip>
      ),
      key: 'loadFromFunction',
      width: 150,
      render: (_text, row) =>
        row.loadFromFunctionEnabled ? (
          <Tooltip title={row.loadFromFunctionRule
            ? `Loaded only when this rule is true: ${row.loadFromFunctionRule}`
            : 'Loaded only when needed (rule evaluated at runtime).'}>
            <span>
              <StatusPill tone="amber">Only when needed</StatusPill>
            </span>
          </Tooltip>
        ) : (
          <Tooltip title="The agent reads this prompt every time it answers a question that uses one of the linked capabilities.">
            <span><StatusPill tone="green">Always</StatusPill></span>
          </Tooltip>
        ),
    },
    {
      title: 'ID',
      dataIndex: 'id',
      key: 'id',
      width: 70,
      render: (v: number) => (
        <Text
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 11,
            color: isDark ? colors.slate[400] : colors.slate[500],
          }}
        >
          #{v}
        </Text>
      ),
    },
    {
      title: '',
      key: 'actions',
      width: 130,
      align: 'right',
      fixed: 'right',
      onCell: () => ({ onClick: (e) => e.stopPropagation() }),
      render: (_text, row) => (
        <Space size={2}>
          <Tooltip title="Edit instructions">
            <Button
              type="primary"
              ghost
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEdit(row)}
            >
              Instructions
            </Button>
          </Tooltip>
          <Dropdown menu={moreMenu(row)} trigger={['click']} placement="bottomRight">
            <Button type="text" size="small" icon={<MoreOutlined />} aria-label="More actions" />
          </Dropdown>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
      <PageHeader
        accent="violet"
        icon={<CodeOutlined />}
        title={
          <Space size={6} align="center">
            <span>System prompts</span>
            <HelpHotspot topic="prompts.system" />
          </Space>
        }
        subtitle={`Reusable guidance blocks. Each agent picks up the ones tied to its capabilities. — ${ctx.selectedMerchant?.title ?? 'this merchant'}.`}
        count={store.totalCount}
        countLabel={store.totalCount === 1 ? 'prompt' : 'prompts'}
        actions={
          <Space>
            <Input.Search
              placeholder="Search system prompts…"
              allowClear
              style={{ width: 280 }}
              value={store.search}
              onChange={(e) => store.setSearch(e.target.value)}
              onSearch={() => void store.fetch()}
            />
            <Tooltip title="A 'global' prompt isn't tied to one specific user audience — it applies to every user the agent talks to. Turn this on to hide audience-scoped prompts (Patient-only, Provider-only) and see just the universal ones.">
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                  fontSize: 13,
                  color: isDark ? colors.slate[300] : colors.slate[600],
                }}
              >
                <Switch
                  size="small"
                  checked={store.globalsOnly}
                  onChange={(v) => {
                    store.setGlobalsOnly(v);
                    void store.fetch();
                  }}
                />
                <GlobalOutlined /> Show only universal prompts
              </span>
            </Tooltip>
            <Button
              icon={<ReloadOutlined />}
              onClick={() => void store.fetch()}
              loading={store.isLoading}
            >
              Refresh
            </Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
              New prompt
            </Button>
          </Space>
        }
      />

      {store.error && (
        <Alert
          type="error"
          message="Failed to load system prompts"
          description={store.error}
          showIcon
          closable
        />
      )}

      <SectionCard noPadding>
        <Table<SystemPromptDto>
          rowKey="id"
          columns={columns}
          dataSource={store.items}
          loading={store.isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            pageSize: 25,
            showSizeChanger: true,
            showTotal: (t) => `${t} system prompts`,
            style: { padding: '12px 18px', margin: 0 },
          }}
          onRow={(row) => ({
            onClick: () => openEdit(row),
            style: { cursor: 'pointer' },
          })}
          locale={{
            emptyText: (
              <BrandedEmpty
                icon={<RightOutlined />}
                title={store.search ? 'No system prompts match' : 'No system prompts yet'}
                description={
                  store.search
                    ? 'Try clearing the search or globals filter.'
                    : 'Create instruction blocks that get composed into your agents via capabilities.'
                }
                action={
                  !store.search ? (
                    <Button type="primary" icon={<PlusOutlined />} onClick={() => openEdit(null)}>
                      Create system prompt
                    </Button>
                  ) : undefined
                }
              />
            ),
          }}
        />
      </SectionCard>

      <SystemPromptEditor
        open={editorOpen}
        prompt={editing}
        onClose={() => {
          setEditorOpen(false);
          setEditing(null);
        }}
      />
    </div>
  );
});

// ── Editor modal (create + edit) ────────────────────────────────────────────

interface SystemPromptEditorProps {
  open: boolean;
  prompt: SystemPromptDto | null;
  onClose: () => void;
}

const SystemPromptEditor = observer(function SystemPromptEditor({
  open,
  prompt,
  onClose,
}: SystemPromptEditorProps) {
  const { systemPromptStore: store } = useStore();
  const [form] = Form.useForm<{
    title: string;
    name?: string;
    baseInstructions: string;
    comments?: string;
    tags?: string;
    loadFromFunctionEnabled: boolean;
    loadFromFunctionRule?: string;
  }>();
  const [submitting, setSubmitting] = useState(false);
  const isEdit = !!prompt;

  const initial = prompt
    ? {
        title: prompt.title ?? '',
        name: prompt.name ?? undefined,
        baseInstructions: prompt.baseInstructions ?? '',
        comments: prompt.comments ?? undefined,
        tags: (prompt.tags ?? []).join(', '),
        loadFromFunctionEnabled: prompt.loadFromFunctionEnabled ?? false,
        loadFromFunctionRule: prompt.loadFromFunctionRule ?? undefined,
      }
    : {
        title: '',
        baseInstructions: '',
        loadFromFunctionEnabled: false,
      };

  async function handleSubmit(): Promise<void> {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const tags = values.tags
        ? values.tags.split(',').map((t) => t.trim()).filter(Boolean)
        : [];
      const payload: Partial<SystemPromptDto> = {
        ...(isEdit ? { id: prompt!.id } : {}),
        title: values.title.trim(),
        name: values.name?.trim() || undefined,
        baseInstructions: values.baseInstructions,
        comments: values.comments?.trim() || undefined,
        tags,
        loadFromFunctionEnabled: values.loadFromFunctionEnabled,
        loadFromFunctionRule: values.loadFromFunctionEnabled
          ? values.loadFromFunctionRule
          : undefined,
      };
      if (isEdit) {
        await store.update(payload);
        message.success('System prompt updated');
      } else {
        await store.create(payload);
        message.success('System prompt created');
      }
      form.resetFields();
      onClose();
    } catch (err: unknown) {
      const isFormError = err && typeof err === 'object' && 'errorFields' in err;
      if (!isFormError) {
        message.error(err instanceof Error ? err.message : 'Failed to save');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <Modal
      open={open}
      onCancel={() => {
        form.resetFields();
        onClose();
      }}
      onOk={handleSubmit}
      okText={isEdit ? 'Save changes' : 'Create system prompt'}
      okButtonProps={{ loading: submitting }}
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <CodeOutlined /> {isEdit ? 'Edit system prompt' : 'New system prompt'}
        </span>
      }
      destroyOnClose
      width={760}
    >
      <Paragraph type="secondary" style={{ fontSize: 13, marginBottom: 14 }}>
        Reusable guidance that the agent reads on every turn. Agents pick this up automatically
        for every capability they have — so this is where you define the agent's voice, the
        rules it follows, and any limits.
      </Paragraph>
      <Form form={form} layout="vertical" preserve={false} initialValues={initial}>
        <Form.Item
          name="title"
          label="Title"
          rules={[{ required: true, message: 'A title is required' }]}
        >
          <Input maxLength={300} placeholder='e.g. "Triage assessment voice"' showCount />
        </Form.Item>
        <Form.Item
          name="name"
          label="Short reference name (optional)"
          extra="A short identifier developers can use to find this prompt. Most authors can leave this blank."
        >
          <Input maxLength={100} placeholder="triage.voice" />
        </Form.Item>
        <Form.Item
          name="baseInstructions"
          label="Instructions"
          rules={[{ required: true, message: 'Instructions are required' }]}
          extra="Write what you want the agent to do, in plain English. The agent reads this every time it answers a question that uses this capability."
        >
          <Input.TextArea rows={10} style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }} />
        </Form.Item>
        <Form.Item
          name="tags"
          label="Tags"
          extra="Add words like 'triage' or 'oncology' so you can find this prompt later. Separate with commas."
        >
          <Input placeholder="triage, oncology, clinical-safety" />
        </Form.Item>
        <Form.Item
          name="comments"
          label="Author comments"
          extra="Notes for other authors — does not get included in agent context."
        >
          <Input.TextArea rows={2} />
        </Form.Item>
        <Form.Item label="Loading strategy">
          <Form.Item name="loadFromFunctionEnabled" valuePropName="checked" noStyle>
            <Switch
              checkedChildren="Conditional"
              unCheckedChildren="Always loaded"
            />
          </Form.Item>
          <Text
            type="secondary"
            style={{ fontSize: 12, marginLeft: 12, display: 'inline-block' }}
          >
            When conditional, the prompt only loads if the rule below evaluates true.
          </Text>
        </Form.Item>
        <Form.Item
          shouldUpdate={(prev, curr) =>
            prev.loadFromFunctionEnabled !== curr.loadFromFunctionEnabled
          }
          noStyle
        >
          {({ getFieldValue }) =>
            getFieldValue('loadFromFunctionEnabled') ? (
              <Form.Item
                name="loadFromFunctionRule"
                label="Conditional load rule"
                extra="Function expression evaluated by the prompt loader at runtime."
              >
                <Input.TextArea
                  rows={3}
                  style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 13 }}
                />
              </Form.Item>
            ) : null
          }
        </Form.Item>
      </Form>
    </Modal>
  );
});
